import { useState } from "react";
import { Box, Typography, Paper, Stack, TextField, MenuItem, Alert, Chip, LinearProgress, Grid2 as Grid } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxFacilityLoadDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";

const bandColor = (b: string) => b === "Crowded" ? "error" : b === "Moderate" ? "warning" : "success";

function FacilityCard({ f }: { f: DxFacilityLoadDto }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>{f.code} — {f.name}</Typography>
        <Chip size="small" label={f.band} color={bandColor(f.band)} />
      </Stack>
      <Typography variant="caption" color="text.secondary">{f.kind}{f.room ? ` · ${f.room}` : ""}</Typography>
      <Box sx={{ my: 1 }}>
        <LinearProgress variant="determinate" value={Math.min(100, f.utilizationPct)} color={bandColor(f.band)} sx={{ height: 8, borderRadius: 1 }} />
        <Typography variant="caption">{f.utilizationPct}% · {f.activeOrders}/{f.capacity} active</Typography>
      </Box>
      <Stack direction="row" spacing={2}>
        <Typography variant="body2">Machines: <b>{f.machineCount}</b></Typography>
        <Typography variant="body2">Est. wait: <b>{f.estimatedWaitMinutes} min</b></Typography>
      </Stack>
    </Paper>
  );
}

export function FacilityLoadPage() {
  const [kind, setKind] = useState("");
  const query = useQuery({ queryKey: ["dx-facility-load", kind], queryFn: () => diagnosticsApi.facilityLoad(kind || undefined), refetchInterval: 15000 });

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Facility load" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select label="Kind" size="small" value={kind} onChange={e => setKind(e.target.value)} sx={{ width: 200 }}>
          {["", "Lab", "Radiology", "Collection", "Other"].map(k => <MenuItem key={k} value={k}>{k || "All"}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>Auto-refreshes every 15s. New orders route to the least-busy facility of the required kind.</Typography>
      </Paper>
      {query.isError && <Alert severity="error">{errorMessage(query.error)}</Alert>}
      {!query.isLoading && (query.data?.length ?? 0) === 0 && <Alert severity="info">No active facilities. Add facilities on the Facilities screen.</Alert>}
      <Grid container spacing={2}>
        {(query.data ?? []).map(f => <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4 }}><FacilityCard f={f} /></Grid>)}
      </Grid>
    </Box>
  );
}
